import { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Clock, CheckCircle, XCircle, Send, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubInvites = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, expired

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    const token = localStorage.getItem('club_token');
    try {
      // Fetch coaches and managers as "invites"
      const [coachesRes, managersRes] = await Promise.all([
        axios.get(`${API}/clubs/coaches`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clubs/managers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      // Transform to invite-like structure
      const coachInvites = (coachesRes.data || []).map(c => ({
        ...c,
        type: 'coach',
        email: c.coach_email,
        name: c.coach_name,
        status: 'accepted' // If they're in the list, they're assigned
      }));

      const managerInvites = (managersRes.data || []).map(m => ({
        ...m,
        type: 'manager',
        email: m.parent_email,
        name: m.parent_name,
        status: m.status || 'active'
      }));

      setInvites([...coachInvites, ...managerInvites]);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
    setLoading(false);
  };

  const handleResend = async (invite) => {
    toast.info('Resend functionality coming soon');
  };

  const handleCancel = async (invite) => {
    if (!confirm(`Remove ${invite.name || invite.email}?`)) return;
    
    const token = localStorage.getItem('club_token');
    try {
      if (invite.type === 'coach') {
        await axios.delete(`${API}/clubs/teams/${invite.team_id}/coaches/${invite.coach_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/clubs/teams/${invite.team_id}/managers/${invite.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Access removed');
      fetchInvites();
    } catch (error) {
      toast.error('Failed to remove access');
    }
  };

  const filteredInvites = invites.filter(invite => {
    if (filter === 'all') return true;
    return invite.status === filter;
  });

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    accepted: 'bg-emerald-500/20 text-emerald-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    expired: 'bg-red-500/20 text-red-400'
  };

  const statusIcons = {
    pending: Clock,
    accepted: CheckCircle,
    active: CheckCircle,
    expired: XCircle
  };

  return (
    <div className="space-y-6" data-testid="club-invites">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Invites & <span className="text-primary">Access Control</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage coach and manager access
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex bg-card border border-border/40 rounded-sm overflow-hidden max-w-md">
        {['all', 'pending', 'accepted', 'expired'].map(tab => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`flex-1 py-2 text-xs uppercase tracking-widest transition-colors ${
              filter === tab 
                ? 'bg-primary/20 text-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/40 rounded-sm p-4 text-center">
          <p className="font-heading text-2xl">{invites.length}</p>
          <p className="text-xs text-muted-foreground uppercase">Total</p>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4 text-center">
          <p className="font-heading text-2xl text-yellow-400">
            {invites.filter(i => i.status === 'pending').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Pending</p>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4 text-center">
          <p className="font-heading text-2xl text-emerald-400">
            {invites.filter(i => i.status === 'accepted' || i.status === 'active').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Active</p>
        </div>
        <div className="bg-card border border-border/40 rounded-sm p-4 text-center">
          <p className="font-heading text-2xl text-primary">
            {invites.filter(i => i.type === 'coach').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Coaches</p>
        </div>
      </div>

      {/* Invites List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredInvites.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <Mail className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground">
            {filter === 'all' 
              ? 'No invites or access records yet' 
              : `No ${filter} invites`}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-card/50 border-b border-border/40">
              <tr>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Person</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Type</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Status</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Date</th>
                <th className="text-right text-xs uppercase tracking-widest text-muted-foreground p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvites.map((invite, idx) => {
                const StatusIcon = statusIcons[invite.status] || Clock;
                return (
                  <motion.tr
                    key={invite.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border-b border-border/20 hover:bg-card/50"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          invite.type === 'coach' ? 'bg-primary/20' : 'bg-secondary/20'
                        }`}>
                          <Mail className={invite.type === 'coach' ? 'text-primary' : 'text-secondary'} size={18} />
                        </div>
                        <div>
                          <p className="font-medium">{invite.name || 'Pending...'}</p>
                          <p className="text-xs text-muted-foreground">{invite.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-sm ${
                        invite.type === 'coach' ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'
                      }`}>
                        {invite.type === 'coach' ? 'Coach' : 'Manager'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-sm flex items-center gap-1 w-fit ${statusColors[invite.status]}`}>
                        <StatusIcon size={12} />
                        {invite.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {invite.assigned_at 
                        ? new Date(invite.assigned_at).toLocaleDateString() 
                        : '-'}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        {invite.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(invite)}
                            className="text-muted-foreground hover:text-primary"
                            title="Resend invite"
                          >
                            <RefreshCw size={14} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancel(invite)}
                          className="text-muted-foreground hover:text-red-400"
                          title="Remove access"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Info */}
      <div className="bg-card border border-border/40 rounded-sm p-4">
        <h3 className="font-semibold mb-2">Access Control</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div>
            <p className="text-primary font-medium mb-1">Coaches</p>
            <p>Full technical access to team workspace, drills, and training plans.</p>
          </div>
          <div>
            <p className="text-secondary font-medium mb-1">Managers</p>
            <p>Logistics-only access: sessions, schedules, and communications.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubInvites;
